import { Injectable, inject } from '@angular/core';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { BuildType, PagedResponse, QueryFiltersParams } from 'mbs-ui-shared@models';
import { ChangeCompanyInfo, CreateUserDto, UpdateUserDto, User, UserComputerData } from 'mbs-ui-shared@models/user';
import { UsersService } from 'mbs-ui-shared@services/users/users.service';
import {
  DeleteUserRequestType,
  DeleteUsersGroupActionParamsType,
  EnableUsersGroupActionParamsType,
  UsersQueryParams
} from 'mbs-ui-shared@services/users/users.types';
import { Observable, of, tap } from 'rxjs';
import { UsersStore } from '../store/users/usersStore';
import { PagingAndDataFacade } from '../store/withPagingAndDataStoreFeature/pagingAndData.facade';

@Injectable({
  providedIn: 'root'
})
export class UsersFacade extends PagingAndDataFacade<User> {
  protected override store = inject(UsersStore);
  private service = inject(UsersService);
  private companiesFacade = inject(CompaniesFacade);
  private computersFacade = inject(ComputersFacade);

  public getUsersCompact(params: UsersQueryParams): Observable<PagedResponse<User>> {
    return this.service.getCompact(params);
  }

  public create(params: CreateUserDto): Observable<User> {
    return this.service.create(params).pipe(tap(() => this.invalidateEntities(true, false)));
  }

  public updateUser(params: UpdateUserDto, id: string): Observable<User> {
    return this.service.updateUser(params, id).pipe(tap((user) => this.invalidateEntities(true, true, user)));
  }

  public enable(user: User, enabled: boolean): Observable<User> {
    return this.service.enable(user, enabled).pipe(tap((user) => this.invalidateEntities(false, false, user)));
  }

  public enableUsers(params: EnableUsersGroupActionParamsType): Observable<null> {
    return this.service.enableUsers(params).pipe(tap(() => this.invalidateEntities(false, false)));
  }

  public delete(params: DeleteUserRequestType): Observable<null> {
    return this.service.deleteUser(params).pipe(tap(() => (!params?.skipInvalidate ? this.invalidateEntities(true, true) : of(null))));
  }

  public getUserComputersData(id: string): Observable<UserComputerData[]> {
    return this.service.getUserComputersData(id);
  }

  public deleteUsers(params: DeleteUsersGroupActionParamsType): Observable<null> {
    return this.service.deleteUsers(params).pipe(tap(() => this.invalidateEntities(true, true)));
  }

  public cancelDeleting(userId: string): Observable<null> {
    return this.service.cancelDeleting(userId);
  }

  public resetPassword(
    userId: string,
    params: { notificationEmails: string[]; signUpNotificationBuildList: BuildType[] }
  ): Observable<null> {
    return this.service.resetPassword(userId, params);
  }

  public getChangeCompanyInfo(userId: string): Observable<ChangeCompanyInfo> {
    return this.service.changeCompanyInfo(userId);
  }

  public export(body: { users?: string[]; filter?: QueryFiltersParams }): Observable<string> {
    return this.service.export(body);
  }

  public updateCompany(userId: string, params: { companyId: string; leaveLicenseInLastCompany?: boolean }): Observable<User> {
    return this.service.updateCompany(userId, params).pipe(tap((user) => this.invalidateEntities(true, true, user)));
  }

  public isUserExists(email: string): Observable<boolean> {
    return this.service.isUserExists(email);
  }

  private invalidateEntities(invalidateCompanies: boolean, invalidateComputers: boolean, user?: User): void {
    this.refreshAfterCRUD(user);
    if (invalidateCompanies) this.companiesFacade.invalidate();
    if (invalidateComputers) this.computersFacade.invalidate();
  }
}
